import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineUsers, HiOutlineUserAdd, HiOutlineTrash, HiOutlinePencil, HiOutlineShieldCheck, HiOutlineRefresh, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import ConfirmModal from '../components/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ROLE_LABELS = {
  admin: { label: 'Admin', icon: '🔧', desc: 'Accès complet — Production + Staging + Gestion utilisateurs' },
  validation: { label: 'Validation', icon: '✅', desc: 'Lecture prod · Accès complet Staging' },
};

export default function UserManagementPage({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'validation' });
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState(null); // { username, role, newPassword }
  const [saving, setSaving] = useState(false);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, warning, onConfirm }

  // Show/hide passwords
  const [showCreatePwd, setShowCreatePwd] = useState(false);
  const [showEditPwd, setShowEditPwd] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/users`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/users`, {
        method: 'POST', headers,
        body: JSON.stringify(newUser),
      });
      const data = await res.json();
      if (res.ok) {
        showMsg('success', data.message);
        setNewUser({ username: '', password: '', role: 'validation' });
        setShowCreate(false);
        fetchUsers();
      } else {
        showMsg('error', data.error);
      }
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (username) => {
    setConfirmModal({
      title: 'Supprimer l\'utilisateur',
      message: `Voulez-vous vraiment supprimer "${username}" ? Cette action est irréversible.`,
      warning: 'Toutes les données associées à cet utilisateur seront définitivement perdues.',
      confirmLabel: 'Supprimer',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const res = await fetch(`${API_BASE}/api/auth/users/${username}`, {
            method: 'DELETE', headers,
          });
          const data = await res.json();
          if (res.ok) {
            showMsg('success', data.message);
            fetchUsers();
          } else {
            showMsg('error', data.error);
          }
        } catch (err) {
          showMsg('error', err.message);
        }
      },
    });
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const body = { role: editUser.role };
      if (editUser.newPassword) body.password = editUser.newPassword;
      const res = await fetch(`${API_BASE}/api/auth/users/${editUser.username}`, {
        method: 'PUT', headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        showMsg('success', data.message);
        setEditUser(null);
        fetchUsers();
      } else {
        showMsg('error', data.error);
      }
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div className="page-title"><HiOutlineUsers className="title-icon" /> Gestion des utilisateurs</div>
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="page-title">
        <HiOutlineUsers className="title-icon" />
        Gestion des utilisateurs
      </div>

      {/* Message */}
      {message && (
        <div className="card" style={{
          marginBottom: 16,
          borderLeft: `4px solid ${message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)'}`,
          padding: '12px 20px',
        }}>
          {message.text}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Total utilisateurs</div>
          <div className="metric-value">{users.length}</div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Administrateurs</div>
          <div className="metric-value">{users.filter(u => u.role === 'admin').length}</div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 8 }}>Validation</div>
          <div className="metric-value">{users.filter(u => u.role === 'validation').length}</div>
        </div>
      </div>

      {/* User list */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title"><HiOutlineShieldCheck className="icon" /> Utilisateurs</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={fetchUsers} style={{ padding: '6px 12px', fontSize: 12 }}>
              <HiOutlineRefresh />
            </button>
            <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)} style={{ padding: '6px 14px', fontSize: 12 }}>
              <HiOutlineUserAdd /> Nouveau
            </button>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Rôle</th>
                <th>Créé le</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.username}>
                  <td style={{ fontWeight: 500 }}>
                    {ROLE_LABELS[u.role]?.icon || '👤'} {u.username}
                  </td>
                  <td>
                    <span className={`badge ${u.role === 'admin' ? 'healthy' : 'degraded'}`}>
                      {ROLE_LABELS[u.role]?.label || u.role}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn-icon" title="Modifier" onClick={() => setEditUser({ ...u, newPassword: '' })}>
                        <HiOutlinePencil />
                      </button>
                      {u.username !== 'admin' && (
                        <button className="btn-icon danger" title="Supprimer" onClick={() => handleDelete(u.username)}>
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <div className="card-title"><HiOutlineUserAdd className="icon" /> Créer un utilisateur</div>
          </div>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Nom d'utilisateur</label>
                <input
                  type="text" required minLength={3}
                  value={newUser.username}
                  onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))}
                  placeholder="ex: jean.dupont"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Mot de passe</label>
                <div className="input-password-wrapper">
                  <input
                    type={showCreatePwd ? 'text' : 'password'} required minLength={4}
                    value={newUser.password}
                    onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                    placeholder="Min. 4 caractères"
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowCreatePwd(p => !p)} tabIndex={-1}>
                    {showCreatePwd ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                  </button>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Rôle</label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}
                  style={{
                    width: '100%', padding: '12px 16px',
                    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
                    fontSize: 14, outline: 'none',
                  }}
                >
                  <option value="validation">✅ Validation</option>
                  <option value="admin">🔧 Admin</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={creating} style={{ height: 46 }}>
                {creating ? '...' : 'Créer'}
              </button>
            </div>
            {/* Role description */}
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
              {ROLE_LABELS[newUser.role]?.desc}
            </div>
          </form>
        </div>
      )}

      {/* Role legend */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Rôles et permissions</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {Object.entries(ROLE_LABELS).map(([key, val]) => (
            <div key={key} style={{ padding: 16, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                {val.icon} {val.label}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {val.desc}
              </div>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                {key === 'admin' ? (
                  <ul style={{ paddingLeft: 16, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                    <li>Production : lecture + écriture (restart, restore)</li>
                    <li>Staging : accès complet</li>
                    <li>Backups : accès complet</li>
                    <li>Gestion utilisateurs</li>
                  </ul>
                ) : (
                  <ul style={{ paddingLeft: 16, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                    <li>Production : lecture seule (overview, système)</li>
                    <li>Staging : accès complet</li>
                    <li>Pas d'accès aux backups</li>
                    <li>Pas d'accès à la gestion utilisateurs</li>
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit modal */}
      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3><HiOutlinePencil style={{ verticalAlign: 'middle', color: 'var(--accent-blue)' }} /> Modifier — {editUser.username}</h3>
            <div style={{ marginTop: 16 }}>
              <div className="form-group">
                <label>Rôle</label>
                <select
                  value={editUser.role}
                  onChange={e => setEditUser(p => ({ ...p, role: e.target.value }))}
                  style={{
                    width: '100%', padding: '12px 16px',
                    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
                    fontSize: 14, outline: 'none',
                  }}
                >
                  <option value="validation">✅ Validation</option>
                  <option value="admin">🔧 Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(laisser vide pour ne pas changer)</span></label>
                <div className="input-password-wrapper">
                  <input
                    type={showEditPwd ? 'text' : 'password'}
                    value={editUser.newPassword}
                    onChange={e => setEditUser(p => ({ ...p, newPassword: e.target.value }))}
                    placeholder="••••••••"
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowEditPwd(p => !p)} tabIndex={-1}>
                    {showEditPwd ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setEditUser(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal (delete) */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        warning={confirmModal?.warning}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}
